import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Select,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core"
import { useCallback, useEffect, useMemo, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { PageHeader, PageLayout } from "../../../components/crud"
import type { Column } from "../../../components/DataTable"
import { DataTable, useDataTable } from "../../../components/DataTable"
import { downloadExport } from "../../../helpers/downloadExport"
import { useUser } from "../../user/hooks/useUser"
import type { Agency } from "../models/agency"
import { AgencySchema, STATUS_MAP } from "../models/agency"
import type { DrawerMode } from "./AgencyDrawer"
import AgencyDrawer from "./AgencyDrawer"

export default function AgencyList() {
  const { t } = useTranslation()
  const { data: userData } = useUser()
  const canManage = userData?.can?.manageAgencies ?? false

  const {
    data,
    isLoading,
    page,
    pageSize,
    sort,
    sortDir,
    handleSortChange,
    handlePageChange,
    filters,
    updateFilters,
  } = useDataTable<Agency>({
    queryKey: "agencies",
    url: "agency",
    schema: AgencySchema,
    defaultSort: { field: "id", dir: "desc" },
    defaultPageSize: 50,
  })

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [drawerOpened, setDrawerOpened] = useState(false)
  const [drawerMode, setDrawerMode] = useState<DrawerMode>("view")
  const params = useParams<{ "*"?: string }>()
  const routeId = params["*"] ? Number(params["*"].split("/")[0]) : NaN

  const openDrawer = useCallback((id: number | null, mode: DrawerMode) => {
    setSelectedId(id)
    setDrawerMode(mode)
    setDrawerOpened(true)
  }, [])

  const closeDrawer = useCallback(() => {
    setDrawerOpened(false)
    setSelectedId(null)
  }, [])

  useEffect(() => {
    if (!Number.isNaN(routeId) && routeId > 0) {
      openDrawer(routeId, "view")
    }
  }, [routeId, openDrawer])

  const handleDeleted = useCallback(() => {
    closeDrawer()
  }, [closeDrawer])
  const handleSwitchToEdit = useCallback(() => {
    setDrawerMode("edit")
  }, [])

  const handleRowClick = useCallback(
    (v: Agency) => {
      if (selectedId === v.id && drawerOpened) {
        closeDrawer()
      } else {
        openDrawer(v.id, "view")
      }
    },
    [openDrawer, closeDrawer, selectedId, drawerOpened],
  )

  const exportFilters = useMemo(() => {
    const f: Record<string, string | undefined> = {}
    if (filters.name) f.name = String(filters.name)
    if (filters.address) f.address = String(filters.address)
    if (filters.phone) f.phone = String(filters.phone)
    if (filters.email) f.email = String(filters.email)
    if (filters.subdomain) f.subdomain = String(filters.subdomain)
    if (filters.status) f.status = String(filters.status)
    return f
  }, [filters])

  const columns: Column<Agency>[] = [
    { key: "id", label: "ID", sortable: true, width: 60 },
    {
      key: "name",
      label: t("agencies.name"),
      sortable: true,
      render: (v) => (
        <Text fw={500} size="sm">
          {v.name}
        </Text>
      ),
    },
    {
      key: "nif",
      label: t("agencies.nif"),
      width: 120,
      render: (v) => <Text size="sm">{v.nif}</Text>,
    },
    {
      key: "email",
      label: t("agencies.email"),
      sortable: true,
      render: (v) => <Text size="sm">{v.email}</Text>,
    },
    {
      key: "district_name",
      label: t("agencies.district"),
      sortable: true,
      render: (v) => <Text size="sm">{v.district_name}</Text>,
    },
    {
      key: "is_master",
      label: t("agencies.type"),
      width: 80,
      render: (v) =>
        v.is_master ? (
          <Badge color="blue" variant="light" size="sm">
            {t("agencies.headOffice")}
          </Badge>
        ) : (
          <Badge color="gray" variant="light" size="sm">
            {t("agencies.branch")}
          </Badge>
        ),
    },
    {
      key: "status",
      label: t("agencies.status"),
      sortable: true,
      width: 90,
      render: (v) => {
        const info = STATUS_MAP[v.status]
        return (
          <Badge color={info?.color ?? "gray"} variant="light" size="sm">
            {t(`agencies.${info?.label ?? "inactive"}`)}
          </Badge>
        )
      },
    },
  ]

  return (
    <PageLayout
      header={
        <PageHeader
          title={t("agencies.title")}
          actions={
            <Group>
              {canManage && (
                <Button
                  variant="default"
                  onClick={() =>
                    downloadExport(
                      "agency/export",
                      "Agencias.xlsx",
                      exportFilters,
                    )
                  }
                  leftSection={<i className="fa fa-file-excel-o" />}
                >
                  <Trans i18nKey="common.export" />
                </Button>
              )}
              {canManage && (
                <Button
                  onClick={() => openDrawer(null, "create")}
                  leftSection={<i className="fa fa-plus" />}
                >
                  <Trans i18nKey="agencies.create" />
                </Button>
              )}
            </Group>
          }
        />
      }
    >
      <Card withBorder p="sm" radius="md" mb="md">
        <Group grow align="flex-end">
          <TextInput
            label={t("agencies.name")}
            placeholder={t("agencies.name")}
            value={(filters.name as string) ?? ""}
            onChange={(e) =>
              updateFilters({ name: e.currentTarget.value || undefined })
            }
          />
          <TextInput
            label={t("agencies.address")}
            placeholder={t("agencies.address")}
            value={(filters.address as string) ?? ""}
            onChange={(e) =>
              updateFilters({ address: e.currentTarget.value || undefined })
            }
          />
          <TextInput
            label={t("agencies.phone")}
            placeholder={t("agencies.phone")}
            value={(filters.phone as string) ?? ""}
            onChange={(e) =>
              updateFilters({ phone: e.currentTarget.value || undefined })
            }
          />
          <TextInput
            label={t("agencies.email")}
            placeholder={t("agencies.email")}
            value={(filters.email as string) ?? ""}
            onChange={(e) =>
              updateFilters({ email: e.currentTarget.value || undefined })
            }
          />
          <TextInput
            label={t("agencies.subdomain")}
            placeholder={t("agencies.subdomain")}
            value={(filters.subdomain as string) ?? ""}
            onChange={(e) =>
              updateFilters({ subdomain: e.currentTarget.value || undefined })
            }
          />
          <Select
            label={t("agencies.status")}
            placeholder={t("agencies.allStatuses")}
            data={[
              { value: "10", label: t("agencies.active") },
              { value: "5", label: t("agencies.inactive") },
            ]}
            clearable
            value={(filters.status as string | undefined) ?? null}
            onChange={(val) => updateFilters({ status: val || undefined })}
          />
        </Group>
      </Card>
      <DataTable<Agency>
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        page={page}
        pageSize={pageSize}
        sort={sort}
        sortDir={sortDir}
        loading={isLoading}
        emptyMessage={t("agencies.empty")}
        keyExtractor={(v) => v.id}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
        actions={(v) =>
          canManage ? (
            <Group gap={4} justify="flex-end" wrap="nowrap">
              <Tooltip label={t("common.edit")}>
                <ActionIcon
                  variant="subtle"
                  size="md"
                  onClick={(e) => {
                    e.stopPropagation()
                    openDrawer(v.id, "edit")
                  }}
                >
                  <i className="fa fa-pencil" />
                </ActionIcon>
              </Tooltip>
            </Group>
          ) : undefined
        }
      />
      <AgencyDrawer
        opened={drawerOpened}
        onClose={closeDrawer}
        mode={drawerMode}
        selectedId={selectedId}
        onDeleted={handleDeleted}
        onSwitchToEdit={handleSwitchToEdit}
      />
    </PageLayout>
  )
}
